// src/components/lead/sections/help/PlatformHelp.tsx
import { useState } from "react";

const PLATFORM_TERMS: { term: string; def: string }[] = [
  {
    term: "Platform View",
    def: "The top-level control center for TurboSignCo platform staff (the team that builds and operates Turbo Assist). Shows cross-org health (orgs, pending approvals, blocked users) and lets platform operators assist subscriber organizations when needed.",
  },
  {
    term: "Subscription costs",
    def: "Platform View → Costs. Ledger of what it costs to run Turbo Assist (email, hosting, database, AI, and similar). Each price is a dated row, so a rate change keeps the previous amount. The monthly run rate adds active monthly plans and one-twelfth of active annual plans. Visible to platform_owner, platform_admin, and platform_dev.",
  },
  {
    term: "Platform roles — platform_admin / platform_dev",
    def: "Your app-level role stored in user_roles (org_id = null), independent of any org membership. Unlocks Platform View. platform_admin: org/user provisioning and cross-org visibility. platform_dev: adds debug/hygiene tools and full platform access.",
  },
  {
    term: "Approve + Sales",
    def: "Sets the user's account to approved and creates an active membership in their org with role = 'sales'. In plain language: approve this account and give them a base Sales role so they can log in and use Lead Workspace.",
  },
  {
    term: "Block",
    def: "Sets the user's access_status to 'blocked' and deactivates their memberships in that org, preventing them from logging in. An optional reason can be recorded for reference.",
  },
  {
    term: "Pending approvals",
    def: "Users whose access_status = 'pending' — they've signed in with a recognized email but no admin or platform operator has approved or blocked them yet.",
  },
  {
    term: "Blocked users",
    def: "Users whose access_status = 'blocked' — explicitly denied access, with an optional blocked_reason logged.",
  },
  {
    term: "Target org (Admin view) vs. cross-org view (Platform)",
    def: "In Admin view you act as org admin for one specific organization. In Platform View you see all orgs at once and can drill into any org's users without switching your own active org.",
  },
  {
    term: "Org hygiene",
    def: "A high-level measure of whether an org is set up and actively using the system — e.g. approvals processed, leads being created/exported, no persistent RPC failures.",
  },
  {
    term: "Knowledge Base — org vs manufacturer / app",
    def: "Articles are tagged with a scope. Org-scoped documents belong to one organization (dealers see only their org’s). Manufacturer- and app-scoped published content is returned broadly to any workspace that queries with a member org — it is not automatically limited by which manufacturer a dealer ‘subscribes’ to in data. Platform roles use a workspace org when editing or testing reads.",
  },
];

export function PlatformHelp() {
  const [expandedTerms, setExpandedTerms] = useState<Set<number>>(new Set());

  return (
    <div className="text-left">
      <div className="flex items-center justify-between">
        <span className="text-blue-900 text-sm">Cross-org operator tools. Click any term to learn more.</span>
        <button
          type="button"
          onClick={() =>
            setExpandedTerms((prev) =>
              prev.size === PLATFORM_TERMS.length
                ? new Set()
                : new Set(PLATFORM_TERMS.map((_, i) => i))
            )
          }
          className="text-xs text-blue-500 hover:text-blue-700 underline ml-3 shrink-0"
        >
          {expandedTerms.size > 0 ? "collapse all" : "expand all"}
        </button>
      </div>
      <div className="mt-2 divide-y divide-blue-100">
        {PLATFORM_TERMS.map((item, i) => (
          <div key={i} className="py-1.5">
            <button
              type="button"
              onClick={() =>
                setExpandedTerms((prev) => {
                  const next = new Set(prev);
                  if (next.has(i)) next.delete(i);
                  else next.add(i);
                  return next;
                })
              }
              className="flex items-center gap-1.5 w-full text-left font-medium text-blue-800 hover:text-blue-600 text-sm"
            >
              <span className="text-blue-400 w-3 shrink-0 text-xs">{expandedTerms.has(i) ? "▾" : "▸"}</span>
              {item.term}
            </button>
            {expandedTerms.has(i) && (
              <div className="mt-1 ml-[18px] text-blue-700 text-sm leading-relaxed">{item.def}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
